import React, { useState, useRef, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Rédige une relance amiable pour un loyer en retard de 15 jours",
  "Génère une mise en demeure pour 2 mois d'impayés",
  "Résume la situation des impayés actuels",
  "Rédige un courrier de renouvellement de bail",
];

export default function AIAssistant({ open, onClose }) {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Bonjour ! Je suis votre assistant IA. Je peux rédiger des relances, résumer la situation d'un locataire, ou générer des courriers professionnels. Comment puis-je vous aider ?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(`s-${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post("/ai/chat", { message: msg, session_id: sessionId.current });
      setMessages((m) => [...m, { role: "assistant", text: r.data.response }]);
    } catch (e) {
      toast.error("Erreur IA: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="ai-panel">
        <SheetHeader className="px-5 py-4 border-b border-zinc-200">
          <SheetTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-zinc-900 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            Assistant IA
          </SheetTitle>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                m.role === "user" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-900"
              }`}>
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-zinc-100 rounded-lg px-3 py-2 text-sm flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Rédaction…
              </div>
            </div>
          )}
        </div>

        {messages.length <= 1 && (
          <div className="px-5 pb-2 grid grid-cols-1 gap-1.5">
            {SUGGESTIONS.map((s, i) => (
              <button key={i} data-testid={`ai-suggestion-${i}`} onClick={() => send(s)} className="text-left text-xs text-zinc-600 border border-zinc-200 rounded-md px-2.5 py-1.5 hover:bg-zinc-100">
                {s}
              </button>
            ))}
          </div>
        )}

        <div className="border-t border-zinc-200 p-3 flex gap-2">
          <Textarea
            data-testid="ai-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Posez votre question…"
            className="min-h-[44px] max-h-32 resize-none"
          />
          <Button data-testid="ai-send-btn" onClick={() => send()} disabled={loading || !input.trim()} size="icon" className="shrink-0">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
